import React from 'react';
import { retryImport, installPreloadErrorReload } from './util/retryImport';
import * as Sentry from "@sentry/react";
import { version } from '../package.json';

Sentry.init({
    dsn: "https://d2db120e414893d4f2693b5ed3db8750@o4510761946906624.ingest.us.sentry.io/4511520378519553",
    enabled: !import.meta.env.DEV,
    release: version,
    environment: import.meta.env.VITE_APP_ENVIRONMENT ?? import.meta.env.MODE,
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    integrations: [
        Sentry.replayIntegration()
    ],
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.,
    // Enable logs to be sent to Sentry
    enableLogs: true
});

import { createRoot } from 'react-dom/client';
import './index.css';

import {browserName, fullBrowserVersion} from "react-device-detect";
import Logo from "./util/Logo";
import {
    getMissingCriticalCapabilities,
    getOptionalCapabilities,
    isCrossSiteApi,
    isInsecureContext,
} from "./util/browserSupport";
import {API_ENDPOINT} from "./util/constants";
import {initAnalytics} from "./util/analytics";

initAnalytics();

// Support is decided by what the browser can actually do, not by which family
// it belongs to: any engine that has IndexedDB, Web Crypto, Web Storage and
// fetch can run the app. Features beyond that baseline (service worker,
// passkeys, live collaboration) degrade at their call sites.
const missingCapabilities = getMissingCriticalCapabilities();
const isSupported = missingCapabilities.length === 0;

// Optional capabilities ride along on every event so Sentry shows what the
// session could actually do, and a hard block is worth its own message.
// crossSiteApi is here too: cookie-based auth silently fails when the page and
// the API are different sites, and that's invisible without it.
Sentry.setContext("browserCapabilities", {
    ...getOptionalCapabilities(),
    crossSiteApi: isCrossSiteApi(API_ENDPOINT),
});
if (!isSupported) {
    Sentry.captureMessage("Browser blocked: missing critical capabilities", {
        level: "warning",
        extra: {
            browserName,
            fullBrowserVersion,
            missing: missingCapabilities.map((capability) => capability.id),
            insecureContext: isInsecureContext(),
        },
    });
}

// Recover app-wide from failed lazy-chunk preloads (vite:preloadError):
// one session-guarded reload, then Vite's default rejection.
installPreloadErrorReload();

const container = document.getElementById('root');
const root = createRoot(container, {
    // Callback called when an error is thrown and not caught by an ErrorBoundary.
    onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
        console.warn("Uncaught error", error, errorInfo.componentStack);
    }),
    // Callback called when React catches an error in an ErrorBoundary.
    onCaughtError: Sentry.reactErrorHandler(),
    // Callback called when React automatically recovers from errors.
    onRecoverableError: Sentry.reactErrorHandler(),
});

if (isSupported) {
    // Loaded on demand so the app graph (Dexie, the store, the crypto
    // helpers) is never evaluated on a browser that can't support it - a
    // module throwing on import would blank the page instead of showing the
    // explanation below.
    // One paced retry before the terminal couldn't-load screen — a chunk
    // fetch lost to a transient network blip should not strand the user
    // there (JURYBOX-WEB-APP-16).
    retryImport(() => import('./components/App')).then(({default: App}) => {
        root.render(
            <div id="page-handle">
                <App/>
            </div>
        );
    }).catch((error) => {
        // A module that fails to load or parse (an engine rejecting newer
        // syntax, a chunk that didn't download) would otherwise leave a blank
        // page with nothing in the console but the raw error.
        console.error("Could not load the application", error);
        Sentry.captureException(error);
        root.render(
            <div id="page-handle">
                <div className="not-supported">
                    <div style={{margin: "20px auto", maxWidth: "225px"}}>
                        <Logo/>
                    </div>
                    <p>Jurybox couldn't finish loading in this browser.</p>
                    <p>
                        Reload the page to try again. If it keeps happening, update
                        your browser to the latest version, or email us
                        at <a href={"mailto:support@juryboxapp.com"}>support@juryboxapp.com</a>.
                    </p>
                </div>
            </div>
        );
    });
} else {
    root.render(
        <div id="page-handle">
            <div className="not-supported">
                <div style={{margin: "20px auto", maxWidth: "225px"}}>
                    <Logo/>
                </div>
                <p>
                    Jurybox can't run in this browser, {browserName} ({fullBrowserVersion}),
                    because it's missing features the app needs:
                </p>
                <ul className="not-supported-list">
                    {missingCapabilities.map(({id, label, detail}) => (
                        <li key={id}><strong>{label}</strong> &mdash; {detail}</li>
                    ))}
                </ul>
                {isInsecureContext() ? (
                    <p>
                        This page was loaded over an insecure connection, which is what
                        disables those features. Reload it over <strong>https</strong> and
                        your browser will most likely work.
                    </p>
                ) : (
                    <p>
                        Updating to the latest version of your browser usually fixes this.
                        If it doesn't, the current versions of&nbsp;
                        <a href={"https://www.google.com/chrome/"}>Chrome</a>,&nbsp;
                        <a href={"https://www.microsoft.com/en-us/edge/download"}>Edge</a>,&nbsp;
                        <a href={"https://www.apple.com/safari/"}>Safari</a>,&nbsp;
                        <a href={"https://www.mozilla.org/en-US/firefox/new/"}>Firefox</a>, and&nbsp;
                        <a href={"https://brave.com/"}>Brave</a> are all supported.
                    </p>
                )}
                <p className="not-supported-note">
                    Private or incognito windows and strict privacy settings can also
                    switch these features off &mdash; if you're using one, try a normal
                    window and allow site data for this site.
                </p>
            </div>
        </div>
    );
}
